import { supabase } from './supabase';

/**
 * Eventlaag gebruiksanalyse (besluit Tom 29-08): betekenisvolle handelingen
 * — scherm geopend, knop geklikt — als dataregels, gebufferd en in bulk
 * weggeschreven. Voor softwareverbetering, niet voor personeelsbeoordeling;
 * er wordt nooit invoer of inhoud gelogd, alleen de actie. Het team is door
 * Tom geïnformeerd. Faalt het wegschrijven, dan verdwijnt de buffer stil —
 * gebruiksdata mag nooit de app hinderen.
 */
interface EventRij {
  user_id: string;
  rol: string | null;
  scherm: string | null;
  actie: string;
  detail: string | null;
  created_at: string;
}

let actieveGebruiker: { id: string; rol: string | null } | null = null;
let huidigScherm: string | null = null;
let buffer: EventRij[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let laatsteKlik = '';
let laatsteKlikOp = 0;

/**
 * Klikgeheugen voor de meldknop (besluit Tom 01-10): de laatste kliks
 * (scherm + knoplabel + tijd) reizen mee met een softwaremelding en met de
 * AI-duidelijkheidscheck. Lokaal, nooit ouder dan een kwartier.
 */
export interface KlikContext {
  scherm: string | null;
  knop: string;
  tijd: string;
}
const KLIK_GEHEUGEN_MAX = 10;
const KLIK_GEHEUGEN_MS = 15 * 60 * 1000;
let klikGeheugen: KlikContext[] = [];

/** De laatste (max. 10) kliks van het afgelopen kwartier, oudste eerst. */
export function laatsteKliks(): KlikContext[] {
  const grens = Date.now() - KLIK_GEHEUGEN_MS;
  klikGeheugen = klikGeheugen.filter((k) => new Date(k.tijd).getTime() >= grens);
  return [...klikGeheugen];
}

function flush() {
  if (!buffer.length) return;
  const batch = buffer;
  buffer = [];
  void supabase
    .from('gebruiksevents')
    .insert(batch)
    .then(({ error }) => {
      if (error) console.debug('gebruiksevents flush overgeslagen:', error.message);
    });
}

export function logEvent(actie: string, detail?: string | null, scherm?: string | null) {
  if (!actieveGebruiker) return;
  if (scherm !== undefined) huidigScherm = scherm;
  buffer.push({
    user_id: actieveGebruiker.id,
    rol: actieveGebruiker.rol,
    scherm: huidigScherm,
    actie,
    detail: detail?.slice(0, 80) ?? null,
    created_at: new Date().toISOString(),
  });
  if (buffer.length >= 50) flush();
}

function klikLabel(el: Element): string | null {
  const knop = el.closest('button, a, [role="button"]');
  if (!knop) return null;
  const label =
    knop.getAttribute('aria-label') ||
    knop.getAttribute('title') ||
    (knop.textContent ?? '').trim().replace(/\s+/g, ' ');
  return label ? label.slice(0, 60) : null;
}

/** Start de registratie voor een ingelogde interne gebruiker. */
export function startGebruiksEvents(gebruiker: { id: string; rol: string | null }) {
  actieveGebruiker = gebruiker;
  if (flushTimer) return; // al gestart (bijv. na profielwissel)

  document.addEventListener(
    'click',
    (e) => {
      const label = e.target instanceof Element ? klikLabel(e.target) : null;
      if (!label) return;
      // Dubbele registratie van dezelfde knop binnen een seconde overslaan.
      const nu = Date.now();
      if (label === laatsteKlik && nu - laatsteKlikOp < 1000) return;
      laatsteKlik = label;
      laatsteKlikOp = nu;
      klikGeheugen.push({ scherm: huidigScherm, knop: label, tijd: new Date(nu).toISOString() });
      if (klikGeheugen.length > KLIK_GEHEUGEN_MAX) klikGeheugen.shift();
      logEvent('klik', label);
    },
    { capture: true, passive: true },
  );

  flushTimer = setInterval(flush, 20_000);
  window.addEventListener('pagehide', flush);
}

export function stopGebruiksEvents() {
  flush();
  actieveGebruiker = null;
}
